import { Button, Code, DataList, Flex, Text } from '@radix-ui/themes'
import { Cross2Icon } from '@radix-ui/react-icons'
import { formatBucketTime, formatLitres, formatNullableLitres } from './format'
import type { LeakV0ChartRow, LeakV0PinnedTarget, LeakV0TransitionMarker } from './chartModel'
import { LeakV0Json } from './LeakV0Json'
import { LeakV0PanelBox } from './LeakV0PanelBox'

interface LeakV0PinnedDetailProps {
  target: LeakV0PinnedTarget
  onClose: () => void
}

const shortId = (id: string): string => id.slice(0, 8)

const EpisodeValue = ({ episodeId }: { episodeId: string | null }) =>
  episodeId === null ? (
    <Text color="gray">none</Text>
  ) : (
    <Code title={episodeId} variant="ghost">
      {shortId(episodeId)}
    </Code>
  )

const BucketDetail = ({ row }: { row: LeakV0ChartRow }) => (
  <DataList.Root size="1" orientation="horizontal">
    <DataList.Item>
      <DataList.Label>Frozen baseline</DataList.Label>
      <DataList.Value>{formatNullableLitres(row.frozenBaselineL)}</DataList.Value>
    </DataList.Item>
    <DataList.Item>
      <DataList.Label>Noise band</DataList.Label>
      <DataList.Value>{row.noiseBandL === null ? '—' : `± ${formatLitres(row.noiseBandL)}`}</DataList.Value>
    </DataList.Item>
    <DataList.Item>
      <DataList.Label>Quality flag</DataList.Label>
      <DataList.Value>{row.qualityFlag}</DataList.Value>
    </DataList.Item>
    <DataList.Item>
      <DataList.Label>Bucket source</DataList.Label>
      <DataList.Value>{row.bucketSource}</DataList.Value>
    </DataList.Item>
    <DataList.Item>
      <DataList.Label>Episode</DataList.Label>
      <DataList.Value>
        <EpisodeValue episodeId={row.episodeId} />
      </DataList.Value>
    </DataList.Item>
  </DataList.Root>
)

const TransitionDetail = ({ marker }: { marker: LeakV0TransitionMarker }) => (
  <Flex direction="column" gap="2">
    <DataList.Root size="1" orientation="horizontal">
      <DataList.Item>
        <DataList.Label>Transition</DataList.Label>
        <DataList.Value>{`${marker.event.fromState ?? '—'} → ${marker.event.toState}`}</DataList.Value>
      </DataList.Item>
      <DataList.Item>
        {/* The one volume on the episode contract that skips the m³→L conversion at the boundary. */}
        <DataList.Label>Volume snapshot</DataList.Label>
        <DataList.Value>{`${marker.event.volumeSnapshot} m³`}</DataList.Value>
      </DataList.Item>
      <DataList.Item>
        <DataList.Label>Episode</DataList.Label>
        <DataList.Value>
          <EpisodeValue episodeId={marker.episodeId} />
        </DataList.Value>
      </DataList.Item>
    </DataList.Root>
    <LeakV0Json value={marker.event.payload} />
  </Flex>
)

export const LeakV0PinnedDetail = ({ target, onClose }: LeakV0PinnedDetailProps) => {
  const atMs = target.kind === 'bucket' ? target.row.atMs : target.marker.atMs

  return (
    <LeakV0PanelBox
      title={`${target.kind === 'bucket' ? 'Bucket' : 'Transition'} · ${formatBucketTime(atMs)}`}
      action={
        <Button size="1" variant="ghost" color="gray" onClick={onClose} aria-label="Close pinned detail">
          <Cross2Icon />
        </Button>
      }
    >
      {target.kind === 'bucket' ? <BucketDetail row={target.row} /> : <TransitionDetail marker={target.marker} />}
    </LeakV0PanelBox>
  )
}
